/**
 * The authored meaning of a solid, as data rather than as its name.
 *
 * ── Why this exists (audit M2) ───────────────────────────────────────────────
 *
 * The farrowing hall's construction in `main.ts` used to decide three physical
 * facts from display names: `startsWith('pen-rail-solid')` put a box in the
 * camera-passing collision group, `startsWith('pen-gate-solid')` picked the
 * collider beat 2 throws away, and `=== 'sibling-taken'` picked the one removed
 * when her littermate is carried out. Renaming a box for readability would have
 * silently let the camera collapse against the rail again (the 1.370 m → 0.550
 * regression `SWEEP_GROUPS` records) or left a gate standing in an open mouth.
 * Nothing but the name tied them together.
 *
 * Names stay on the lists for inspection, diagnostics and the stealth occluder
 * filters (whose prefix matching is deliberate and documented where it lives).
 * What a box DOES is now the `camera` and `role` fields, read only through the
 * helpers below. `tests/e2e/solid-roles.spec.ts` asserts both halves: the fields
 * select exactly the solids the old prefixes did, and renaming changes nothing.
 */

/**
 * A handle `main.ts` must keep to one specific collider after construction.
 *
 * - `'shut-pen-gate'` — the hall's axis-aligned SHUT gate, replaced by a
 *   rotating one when beat 2 opens it.
 * - `'taken-sibling'` — the littermate's body, removed when she is taken, so
 *   her absence is physical as well as visual.
 * - `'walkway-door'` — the walkway door's leaf, SHUT, replaced by a swinging
 *   one when beat 2's worker walks through it and leaves it ajar.
 */
export type SolidRole = 'shut-pen-gate' | 'taken-sibling' | 'walkway-door';

/** True if the camera sweep should pass through this solid (it stays solid to her). */
export function passesCameraSweep(solid: { readonly camera?: 'passes' }): boolean {
  return solid.camera === 'passes';
}

/**
 * The one solid in `solids` carrying `role`, or a construction-time throw.
 *
 * Exactly one is required, not "the last one wins": `main.ts` keeps a single
 * collider per role, so a second match would leave the first as a collider
 * nobody can ever remove — a gate standing invisibly in a mouth beat 2 opened —
 * and zero would leave the beat's removal a silent no-op. Both are bugs you
 * would otherwise find by walking into air. `solid-roles.spec.ts` asserts the
 * throw for both counts.
 */
export function soleSolidWithRole<T extends { readonly name: string; readonly role?: SolidRole }>(
  solids: readonly T[],
  role: SolidRole,
  listName: string,
): T {
  const matches = solids.filter((solid) => solid.role === role);
  const [only] = matches;
  if (matches.length !== 1 || only === undefined) {
    throw new Error(
      `${listName}: expected exactly one solid with role '${role}', found ${String(matches.length)}`
      + (matches.length > 0 ? ` (${matches.map((solid) => solid.name).join(', ')})` : ''),
    );
  }
  return only;
}
